import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Video, MessageSquare, Settings2, Plus, Pencil, Trash2, ToggleLeft, ToggleRight, Clock, DollarSign, Calendar, Globe, MapPin, X, Inbox, Check, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import ImageUpload from './ImageUpload';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_OPTIONS = [
  { id: 'audio', label: 'Audio Call', icon: Phone, color: '#22C55E' },
  { id: 'video', label: 'Video Call', icon: Video, color: '#3B82F6' },
  { id: 'text', label: 'Text/Chat', icon: MessageSquare, color: '#F59E0B' },
  { id: 'custom', label: 'Custom', icon: Settings2, color: '#8B5CF6' },
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const TradeSetup = ({ profile, setProfile }) => {
  const [services, setServices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [tab, setTab] = useState('services');
  const [tradeBio, setTradeBio] = useState(profile?.trade_bio || '');
  const [portfolio, setPortfolio] = useState(profile?.trade_portfolio || []);
  const [formData, setFormData] = useState({
    type: 'audio', custom_type_name: '', title: '', description: '',
    duration_minutes: 30, price: '', availability_days: [], mode: 'online',
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [sRes, rRes] = await Promise.all([
        axios.get(`${API}/api/trade/services`),
        axios.get(`${API}/api/trade/requests`),
      ]);
      setServices(sRes.data);
      setRequests(rRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const openAdd = () => {
    setEditingService(null);
    setFormData({ type: 'audio', custom_type_name: '', title: '', description: '', duration_minutes: 30, price: '', availability_days: [], mode: 'online' });
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditingService(s);
    setFormData({ type: s.type, custom_type_name: s.custom_type_name || '', title: s.title, description: s.description || '', duration_minutes: s.duration_minutes, price: s.price.toString(), availability_days: s.availability_days || [], mode: s.mode || 'online' });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.price) { toast.error('Title and price are required'); return; }
    const payload = { ...formData, price: parseFloat(formData.price) };
    try {
      if (editingService) {
        await axios.put(`${API}/api/trade/services/${editingService.id}`, payload);
        toast.success('Service updated');
      } else {
        await axios.post(`${API}/api/trade/services`, payload);
        toast.success('Service created');
      }
      fetchData();
      setDialogOpen(false);
    } catch (e) { toast.error('Failed to save'); }
  };

  const toggleActive = async (s) => {
    await axios.put(`${API}/api/trade/services/${s.id}`, { is_active: !s.is_active });
    fetchData();
  };

  const deleteService = async (id) => {
    await axios.delete(`${API}/api/trade/services/${id}`);
    fetchData();
    toast.success('Deleted');
  };

  const updateRequestStatus = async (id, status) => {
    await axios.put(`${API}/api/trade/requests/${id}/status`, { new_status: status });
    fetchData();
    toast.success(`Request ${status}`);
  };

  const saveBioAndPortfolio = async () => {
    try {
      await axios.put(`${API}/api/profile`, { trade_bio: tradeBio, trade_portfolio: portfolio });
      toast.success('Saved');
    } catch (e) { toast.error('Failed to save'); }
  };

  const addPortfolioItem = () => {
    setPortfolio(prev => [...prev, { id: Date.now().toString(), title: '', description: '', image_url: '' }]);
  };

  const updatePortfolio = (idx, field, val) => {
    setPortfolio(prev => prev.map((p, i) => i === idx ? { ...p, [field]: val } : p));
  };

  const removePortfolio = (idx) => { setPortfolio(prev => prev.filter((_, i) => i !== idx)); };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      availability_days: prev.availability_days.includes(day)
        ? prev.availability_days.filter(d => d !== day)
        : [...prev.availability_days, day],
    }));
  };

  const getTypeConfig = (type) => TYPE_OPTIONS.find(t => t.id === type) || TYPE_OPTIONS[3];

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  if (loading) return <div className="text-center text-[#71717A] py-12">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Trade</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-[#121212] p-1 rounded-xl border border-[#27272A]">
        {[
          { id: 'services', label: 'Services', count: services.length },
          { id: 'portfolio', label: 'Portfolio' },
          { id: 'requests', label: 'Requests', count: pendingCount },
        ].map(t => (
          <button key={t.id} data-testid={`trade-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${tab === t.id ? 'bg-violet-500 text-white' : 'text-[#A1A1AA] hover:text-white'}`}>
            {t.label} {t.count > 0 && <span className="ml-1 bg-white/20 px-1.5 py-0.5 rounded-full text-xs">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* ═══ SERVICES TAB ═══ */}
      {tab === 'services' && (
        <div className="space-y-4">
          <Button data-testid="add-trade-service" onClick={openAdd} className="w-full bg-violet-600 hover:bg-violet-700 rounded-xl">
            <Plus className="w-4 h-4 mr-2" /> Add Service
          </Button>

          <AnimatePresence>
            {services.map(s => {
              const tc = getTypeConfig(s.type);
              const Icon = tc.icon;
              return (
                <motion.div key={s.id} data-testid={`trade-service-${s.id}`}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  className="bg-[#121212] rounded-xl border border-[#27272A] p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${tc.color}20` }}>
                      <Icon className="w-5 h-5" style={{ color: tc.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-white font-semibold text-sm truncate">{s.title}</h3>
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${tc.color}15`, color: tc.color }}>
                          {s.type === 'custom' ? s.custom_type_name : tc.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-[#71717A]">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{s.duration_minutes} min</span>
                        <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />${s.price}</span>
                        <span className="flex items-center gap-1">{s.mode === 'online' ? <Globe className="w-3 h-3" /> : s.mode === 'offline' ? <MapPin className="w-3 h-3" /> : <><Globe className="w-3 h-3" /><MapPin className="w-3 h-3" /></>}{s.mode}</span>
                      </div>
                      {s.availability_days?.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {DAYS.map(d => (
                            <span key={d} className={`text-[10px] px-1.5 py-0.5 rounded ${s.availability_days.includes(d) ? 'bg-violet-500/20 text-violet-400' : 'bg-white/5 text-[#52525B]'}`}>
                              {d.slice(0, 2).toUpperCase()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => toggleActive(s)} className="p-1.5 rounded-lg hover:bg-white/5">
                        {s.is_active ? <ToggleRight className="w-5 h-5 text-green-400" /> : <ToggleLeft className="w-5 h-5 text-[#52525B]" />}
                      </button>
                      <button data-testid={`edit-trade-${s.id}`} onClick={() => openEdit(s)} className="p-1.5 rounded-lg hover:bg-white/5">
                        <Pencil className="w-4 h-4 text-[#71717A]" />
                      </button>
                      <button data-testid={`delete-trade-${s.id}`} onClick={() => deleteService(s.id)} className="p-1.5 rounded-lg hover:bg-red-500/10">
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* ═══ PORTFOLIO TAB ═══ */}
      {tab === 'portfolio' && (
        <div className="space-y-4">
          <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 space-y-4">
            <div>
              <Label className="text-[#EDEDED]">Expertise Bio</Label>
              <Textarea data-testid="trade-bio" value={tradeBio} onChange={(e) => setTradeBio(e.target.value)}
                className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white min-h-[80px]"
                placeholder="Describe your expertise, experience, and what you can help with..." rows={3} />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-[#EDEDED]">Portfolio Items</Label>
                <Button data-testid="add-portfolio-item" size="sm" variant="outline" onClick={addPortfolioItem}
                  className="border-[#27272A] text-[#A1A1AA] hover:text-white h-7 text-xs">
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>

              <div className="space-y-3">
                {portfolio.map((item, idx) => (
                  <div key={item.id || idx} className="bg-[#1E1E1E] rounded-xl border border-[#27272A] p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <Input value={item.title} onChange={(e) => updatePortfolio(idx, 'title', e.target.value)}
                        className="bg-transparent border-none text-white text-sm font-semibold p-0 h-auto focus-visible:ring-0"
                        placeholder="Project/Achievement title" />
                      <button onClick={() => removePortfolio(idx)} className="p-1 rounded-lg hover:bg-red-500/10 ml-2">
                        <X className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                    <Textarea value={item.description || ''} onChange={(e) => updatePortfolio(idx, 'description', e.target.value)}
                      className="bg-[#121212] border-[#27272A] text-white text-xs min-h-[50px]"
                      placeholder="Describe what you did..." rows={2} />
                    <ImageUpload value={item.image_url || ''} onChange={(url) => updatePortfolio(idx, 'image_url', url)}
                      label="Portfolio Image" shape="rect" />
                  </div>
                ))}
              </div>
            </div>

            <Button data-testid="save-portfolio" onClick={saveBioAndPortfolio} className="w-full bg-violet-600 hover:bg-violet-700 rounded-xl">
              Save Portfolio
            </Button>
          </div>
        </div>
      )}

      {/* ═══ REQUESTS TAB ═══ */}
      {tab === 'requests' && (
        <div className="space-y-3">
          {requests.length === 0 ? (
            <div className="text-center py-12 text-[#71717A]">
              <Inbox className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No session requests yet</p>
            </div>
          ) : requests.map(r => (
            <div key={r.id} data-testid={`trade-request-${r.id}`}
              className="bg-[#121212] rounded-xl border border-[#27272A] p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-white font-semibold text-sm">{r.customer_name}</p>
                  <p className="text-[#71717A] text-xs">{r.customer_email}</p>
                  <p className="text-[#A1A1AA] text-xs mt-1">{r.service_title} — {r.service_duration}min — ${r.service_price}</p>
                  {r.customer_message && <p className="text-[#71717A] text-xs mt-2 italic">"{r.customer_message}"</p>}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === 'pending' ? 'bg-yellow-500/15 text-yellow-400' : r.status === 'accepted' ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>
                  {r.status}
                </span>
              </div>
              {r.status === 'pending' && (
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={() => updateRequestStatus(r.id, 'accepted')}
                    className="flex-1 bg-green-600 hover:bg-green-700 h-8 text-xs rounded-lg">
                    <Check className="w-3 h-3 mr-1" /> Accept
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => updateRequestStatus(r.id, 'declined')}
                    className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 text-xs rounded-lg">
                    <XCircle className="w-3 h-3 mr-1" /> Decline
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ═══ ADD/EDIT DIALOG ═══ */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{editingService ? 'Edit Service' : 'Add Service'}</DialogTitle>
            <DialogDescription className="text-[#71717A]">Set up a service you offer</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Type selector */}
            <div>
              <Label className="text-[#EDEDED]">Type</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {TYPE_OPTIONS.map(t => {
                  const Icon = t.icon;
                  return (
                    <button key={t.id} data-testid={`type-${t.id}`} onClick={() => setFormData(p => ({ ...p, type: t.id }))}
                      className="p-2 rounded-xl border-2 flex flex-col items-center gap-1 transition-all"
                      style={{ borderColor: formData.type === t.id ? t.color : '#27272A', backgroundColor: formData.type === t.id ? `${t.color}10` : 'transparent' }}>
                      <Icon className="w-4 h-4" style={{ color: t.color }} />
                      <span className="text-[10px] text-[#A1A1AA]">{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {formData.type === 'custom' && (
              <div>
                <Label className="text-[#EDEDED]">Custom Type Name</Label>
                <Input data-testid="custom-type-name" value={formData.custom_type_name}
                  onChange={(e) => setFormData(p => ({ ...p, custom_type_name: e.target.value }))}
                  className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="e.g. In-person Meeting" />
              </div>
            )}

            <div>
              <Label className="text-[#EDEDED]">Topic / Title</Label>
              <Input data-testid="trade-title" value={formData.title}
                onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="e.g. Business Development Strategy" />
            </div>

            <div>
              <Label className="text-[#EDEDED]">Description</Label>
              <Textarea data-testid="trade-description" value={formData.description}
                onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="What will you cover in this session?" rows={2} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#EDEDED]">Duration (min)</Label>
                <Input data-testid="trade-duration" type="number" value={formData.duration_minutes}
                  onChange={(e) => setFormData(p => ({ ...p, duration_minutes: parseInt(e.target.value) || 30 }))}
                  className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" />
              </div>
              <div>
                <Label className="text-[#EDEDED]">Price ($)</Label>
                <Input data-testid="trade-price" type="number" value={formData.price}
                  onChange={(e) => setFormData(p => ({ ...p, price: e.target.value }))}
                  className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="50" />
              </div>
            </div>

            {/* Mode */}
            <div>
              <Label className="text-[#EDEDED]">Mode</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {[{ id: 'online', icon: Globe, label: 'Online' }, { id: 'offline', icon: MapPin, label: 'Offline' }, { id: 'both', label: 'Both', icon: Globe }].map(m => {
                  const Icon = m.icon;
                  return (
                    <button key={m.id} data-testid={`mode-${m.id}`} onClick={() => setFormData(p => ({ ...p, mode: m.id }))}
                      className={`p-2 rounded-xl border-2 flex items-center justify-center gap-1 text-xs transition-all ${formData.mode === m.id ? 'border-violet-500 bg-violet-500/10 text-violet-400' : 'border-[#27272A] text-[#71717A]'}`}>
                      <Icon className="w-3.5 h-3.5" /> {m.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Availability Days */}
            <div>
              <Label className="text-[#EDEDED]">Available Days</Label>
              <div className="flex gap-1.5 mt-2">
                {DAYS.map(d => (
                  <button key={d} data-testid={`day-${d}`} onClick={() => toggleDay(d)}
                    className={`w-9 h-9 rounded-lg text-xs font-medium transition-all ${formData.availability_days.includes(d) ? 'bg-violet-500 text-white' : 'bg-[#1E1E1E] text-[#71717A] border border-[#27272A]'}`}>
                    {d.slice(0, 2).toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button data-testid="save-trade-service" onClick={handleSubmit} className="w-full bg-violet-600 hover:bg-violet-700 rounded-xl">
              {editingService ? 'Update Service' : 'Create Service'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TradeSetup;
